/**
 * Reading and changing the active theme in a live document.
 *
 * Theme is a class on `<html>` rather than React state, because the blocking inline script sets it
 * before any JavaScript module loads and React has to agree with what is already there. So the DOM is
 * the store, and `useTheme` subscribes to it rather than owning a copy — a second copy is how a theme
 * toggle ends up disagreeing with the page it is on.
 *
 * The custom event is what makes the subscription work. `classList` changes fire nothing, and a
 * `MutationObserver` on `<html>` would wake on every unrelated class change; every write goes through
 * `applyTheme`, so one event on one target is enough.
 */
import { themeClasses } from '../tokens/themes.js';
import { allThemeClasses, resolveTheme, themeStorageKey } from './resolve.js';
export const themeChangeEvent = 'apuestapr:themechange';
/**
 * The theme the document is currently in.
 *
 * `fallback` covers the window before the blocking script has run — during a jsdom test, or in a
 * document that never installed the script — so a component always gets a real theme rather than
 * `undefined`.
 */
export const readTheme = (documentElement, fallback = 'light') => {
    for (const [name, className] of Object.entries(themeClasses)) {
        if (documentElement.classList.contains(className))
            return name;
    }
    return fallback;
};
/** Set the theme, persist it, and tell subscribers. The only sanctioned way to change themes. */
export const applyTheme = (theme, target = document) => {
    target.documentElement.classList.remove(...allThemeClasses);
    target.documentElement.classList.add(themeClasses[theme]);
    target.documentElement.style.colorScheme = theme;
    try {
        target.defaultView?.localStorage.setItem(themeStorageKey, theme);
    }
    catch {
        // Storage can be unavailable — private mode, a blocked third-party context, a full quota. The
        // theme still applies for this page; only the persistence is lost, and losing it silently is
        // better than a toggle that throws.
    }
    target.defaultView?.dispatchEvent(new CustomEvent(themeChangeEvent, { detail: theme }));
};
/**
 * Apply the theme the stored preference and the operating system imply. What the blocking script does
 * inline, exposed as a function so the same decision is testable and so an app can re-run it.
 */
export const initializeTheme = (fallback, target = document) => {
    const view = target.defaultView;
    const readStored = () => {
        try {
            return view?.localStorage.getItem(themeStorageKey) ?? null;
        }
        catch {
            // Storage can throw on read as well as on write — a blocked third-party context raises a
            // SecurityError. No stored preference is the right reading of that.
            return null;
        }
    };
    const stored = readStored();
    const theme = resolveTheme({
        stored,
        // Optional call, not defensive padding: jsdom does not implement `matchMedia` at all, so this is
        // the path every component test in the repo takes. No media query means no expressed preference,
        // which the resolver reads as "use the app's default".
        prefersDark: view?.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
        fallback,
    });
    applyTheme(theme, target);
    return theme;
};
export const subscribeToTheme = (listener, view = window) => {
    view.addEventListener(themeChangeEvent, listener);
    return () => {
        view.removeEventListener(themeChangeEvent, listener);
    };
};
//# sourceMappingURL=current.js.map